import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

// Handles drop zones for employees in Plantafel trade-day cells (vertical month view)
export default class extends Controller {
  static values = {
    taskId: Number,
    tradeId: Number,
    startDate: String,
    endDate: String,
  }

  connect() {
    this.dragCounter = 0
  }

  dragover(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  dragenter(event) {
    event.preventDefault()
    this.dragCounter++

    // Show drop indicator
    const indicator = this.element.querySelector(".plantafel-drop-indicator")
    if (indicator) {
      indicator.classList.remove("hidden")
    }
    this.element.classList.add("ring-2", "ring-bautask-green", "bg-green-50")
  }

  dragleave(_event) {
    this.dragCounter--

    if (this.dragCounter === 0) {
      // Hide drop indicator
      const indicator = this.element.querySelector(".plantafel-drop-indicator")
      if (indicator) {
        indicator.classList.add("hidden")
      }
      this.element.classList.remove("ring-2", "ring-bautask-green", "bg-green-50")
    }
  }

  drop(event) {
    event.preventDefault()
    this.dragCounter = 0

    // Hide drop indicator
    const indicator = this.element.querySelector(".plantafel-drop-indicator")
    if (indicator) {
      indicator.classList.add("hidden")
    }
    this.element.classList.remove("ring-2", "ring-bautask-green", "bg-green-50")

    try {
      const jsonString = event.dataTransfer.getData("application/json")

      if (!jsonString) {
        return
      }

      const data = JSON.parse(jsonString)

      // Only handle employee drops
      if (data.resourceType !== "Employee") {
        return
      }

      const { resourceId, resourceName } = data

      if (
        !this.taskIdValue ||
        !this.tradeIdValue ||
        !this.startDateValue ||
        !this.endDateValue ||
        !resourceId
      ) {
        Honeybadger.notify("Missing required data for trade-day assignment", {
          context: {
            level: "warning",
            component: "plantafel-trade-drop",
            taskId: this.taskIdValue,
            tradeId: this.tradeIdValue,
            startDate: this.startDateValue,
            endDate: this.endDateValue,
            resourceId,
          },
        })
        return
      }

      // Assign employee to entire trade date range
      this.assignEmployeeToRange(resourceId, resourceName)
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "plantafel-trade-drop", action: "handle_drop" },
      })
    }
  }

  formatDateForApi(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  getViewStartDate() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get("date") || this.formatDateForApi(new Date())
  }

  assignEmployeeToRange(employeeId, employeeName, force = false) {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    const url = `/plantafel/tasks/${this.taskIdValue}/trades/${this.tradeIdValue}/assign_range`
    const data = {
      employee_id: employeeId,
      start_date: this.startDateValue,
      end_date: this.endDateValue,
      force: force,
      view_mode: "vertical_month",
      view_start_date: this.getViewStartDate(),
    }

    // Show loading indicator
    this.showLoadingFeedback()

    fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        const contentType = response.headers.get("Content-Type") || ""
        const html = await response.text()

        // Check if response contains a mismatch modal
        if (html.includes("plantafel-trade-mismatch-modal") && !force) {
          // Show the mismatch modal - it will handle the force assignment
          Turbo.renderStreamMessage(html)
          return
        }

        if (contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(html)
        } else if (!response.ok) {
          Honeybadger.notify(`Failed to assign employee to trade range: ${response.status}`, {
            context: { level: "error", component: "plantafel-trade-drop", action: "assign_range" },
          })
        }
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "plantafel-trade-drop", action: "assign_range" },
        })
      })
  }

  unassign(event) {
    const assignmentId = event.currentTarget.dataset.assignmentId
    if (!assignmentId) return

    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    const url = `/plantafel/trade_day_assignments/${assignmentId}`

    fetch(url, {
      method: "DELETE",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then(async (response) => {
        const contentType = response.headers.get("Content-Type") || ""
        const html = await response.text()

        if (contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(html)
        } else if (!response.ok) {
          Honeybadger.notify(`Failed to unassign employee from trade-day: ${response.status}`, {
            context: { level: "error", component: "plantafel-trade-drop", action: "unassign" },
          })
        }
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "plantafel-trade-drop", action: "unassign" },
        })
      })
  }

  showLoadingFeedback() {
    const indicator = document.createElement("span")
    indicator.className = "text-xs text-gray-500 animate-pulse"
    indicator.textContent = "..."
    this.element.appendChild(indicator)
    setTimeout(() => indicator.remove(), 2000)
  }
}
